import { useCallback } from 'react';

/**
 * Custom React hook to play a sound from a given URL using the Web Audio API.
 *
 * Everything is lazy: the (single, shared) AudioContext is created and the
 * audio file is fetched + decoded on the first call to `play`, not on mount.
 * Decoded buffers are cached per URL, and a suspended context is resumed
 * before playback. All failures are swallowed quietly.
 *
 * @param url - The URL of the audio file to load and play.
 * @returns A function that, when called, plays the sound.
 *
 * @example
 * ```tsx
 * const playClick = useSound('/sounds/click.mp3');
 * // Later in an event handler:
 * playClick();
 * ```
 */

let sharedAudioCtx: AudioContext | null = null;

const bufferCache = new Map<string, Promise<AudioBuffer>>();

/**
 * Raw (undecoded) audio data cache. Lets `prefetchSfx` warm the network side
 * without creating an AudioContext (which would trigger a Chrome warning
 * before a user gesture). Entries are consumed once by the decode path.
 */
const arrayBufferCache = new Map<string, Promise<ArrayBuffer>>();

function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  let data = arrayBufferCache.get(url);

  if (!data) {
    data = fetch(url).then((res) => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      return res.arrayBuffer();
    });

    // Allow a retry on the next attempt if fetching fails.
    data.catch(() => arrayBufferCache.delete(url));

    arrayBufferCache.set(url, data);
  }

  return data;
}

function getAudioContext(): AudioContext | null {
  if (sharedAudioCtx) {
    return sharedAudioCtx;
  }

  const AudioContextClass =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;

  if (!AudioContextClass) {
    return null;
  }

  sharedAudioCtx = new AudioContextClass();
  return sharedAudioCtx;
}

function loadBuffer(audioCtx: AudioContext, url: string): Promise<AudioBuffer> {
  let buffer = bufferCache.get(url);

  if (!buffer) {
    buffer = fetchArrayBuffer(url).then((data) => {
      // decodeAudioData detaches the ArrayBuffer, so consume the cached copy;
      // a failed decode simply refetches on the next play.
      arrayBufferCache.delete(url);
      return audioCtx.decodeAudioData(data);
    });

    // Allow a retry on the next play if fetching/decoding fails.
    buffer.catch(() => bufferCache.delete(url));

    bufferCache.set(url, buffer);
  }

  return buffer;
}

export function useSound(url: string) {
  const play = useCallback(
    async (volume = 1) => {
      try {
        const audioCtx = getAudioContext();
        if (!audioCtx) {
          return;
        }

        if (audioCtx.state === 'suspended') {
          await audioCtx.resume();
        }

        const buffer = await loadBuffer(audioCtx, url);

        const source = audioCtx.createBufferSource();
        const gainNode = audioCtx.createGain();

        source.buffer = buffer;
        gainNode.gain.value = volume;

        source.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        source.start(0);
      } catch {
        // Silently ignore audio failures.
      }
    },
    [url]
  );

  return play;
}

/* -------------------------------------------------------------------------- */
/* UI sound effects                                                           */
/* -------------------------------------------------------------------------- */

export type SfxName = 'click' | 'toggle' | 'open' | 'close' | 'key';

const SFX_REGISTRY: Record<SfxName, { urls: string[]; volume: number }> = {
  click: {
    urls: ['/audio/sfx/click1.mp3', '/audio/sfx/click2.mp3'],
    volume: 0.5,
  },
  toggle: {
    urls: ['/audio/sfx/toggle1.mp3', '/audio/sfx/toggle2.mp3'],
    volume: 0.5,
  },
  open: { urls: ['/audio/sfx/open.mp3'], volume: 0.55 },
  close: { urls: ['/audio/sfx/close.mp3'], volume: 0.55 },
  key: {
    urls: [
      '/audio/sfx/key1.mp3',
      '/audio/sfx/key2.mp3',
      '/audio/sfx/key3.mp3',
      '/audio/sfx/key4.mp3',
      '/audio/sfx/key5.mp3',
      '/audio/sfx/key6.mp3',
    ],
    volume: 0.35,
  },
};

/** Minimum gap between two plays of the same sound. Extra plays are dropped. */
const SFX_MIN_GAP_MS: Record<SfxName, number> = {
  click: 70,
  toggle: 70,
  open: 70,
  close: 70,
  key: 30,
};

const sfxLastPlayedAt = new Map<SfxName, number>();

async function playSfxAsync(name: SfxName): Promise<void> {
  try {
    const audioCtx = getAudioContext();
    if (!audioCtx) {
      return;
    }

    if (audioCtx.state === 'suspended') {
      await audioCtx.resume();
    }

    const { urls, volume } = SFX_REGISTRY[name];
    const url = urls[Math.floor(Math.random() * urls.length)];
    const buffer = await loadBuffer(audioCtx, url);

    const source = audioCtx.createBufferSource();
    const gainNode = audioCtx.createGain();

    source.buffer = buffer;
    if (name === 'key') {
      // Slight pitch jitter so rapid keystrokes never sound identical.
      source.playbackRate.value = 0.94 + Math.random() * 0.12;
    }
    gainNode.gain.value = volume;

    source.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    source.start(0);
  } catch {
    // Silently ignore audio failures.
  }
}

/**
 * Fire-and-forget UI sound effect. Rate-limited per sound name, silent no-op
 * on the server and on any failure.
 */
export function playSfx(name: SfxName): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const now = performance.now();
    const last = sfxLastPlayedAt.get(name);
    if (last !== undefined && now - last < SFX_MIN_GAP_MS[name]) {
      return;
    }
    sfxLastPlayedAt.set(name, now);

    void playSfxAsync(name);
  } catch {
    // Silently ignore audio failures.
  }
}

/**
 * Warms the HTTP side of every sound effect (fetch + ArrayBuffer only).
 * Deliberately does NOT create an AudioContext: doing so before a user
 * gesture triggers a Chrome console warning. Decoding happens lazily on the
 * first `playSfx` call, consuming these prefetched buffers.
 */
export function prefetchSfx(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    for (const { urls } of Object.values(SFX_REGISTRY)) {
      for (const url of urls) {
        // Only fetch URLs not already decoded (or in-flight).
        if (!bufferCache.has(url)) {
          fetchArrayBuffer(url).catch(() => {
            // Silently ignore prefetch failures.
          });
        }
      }
    }
  } catch {
    // Silently ignore prefetch failures.
  }
}
